import { useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import useCanvasActivity from "@/hooks/useCanvasActivity";

function TorusKnot() {
  const ref = useRef();
  useFrame((state, delta) => {
    ref.current.rotation.y += delta * 0.5;
    ref.current.rotation.x += delta * 0.2;
  });
  return (
    <mesh ref={ref}>
      <torusKnotGeometry args={[0.9, 0.28, 140, 18]} />
      <meshBasicMaterial wireframe color="#00FF66" transparent opacity={0.55} />
    </mesh>
  );
}

function PulseCore() {
  const core = useRef();
  const shell = useRef();
  useFrame((state, delta) => {
    const t = state.clock.elapsedTime;
    const s = 1 + Math.sin(t * 2.4) * 0.18;
    core.current.scale.setScalar(s);
    core.current.rotation.y += delta * 0.8;
    shell.current.rotation.y -= delta * 0.25;
    shell.current.rotation.x += delta * 0.1;
  });
  return (
    <group>
      <mesh ref={core}>
        <icosahedronGeometry args={[0.65, 0]} />
        <meshBasicMaterial wireframe color="#00F0FF" transparent opacity={0.9} />
      </mesh>
      <mesh ref={shell}>
        <icosahedronGeometry args={[1.35, 1]} />
        <meshBasicMaterial wireframe color="#00FF66" transparent opacity={0.3} />
      </mesh>
    </group>
  );
}

const TOOL_MODULES = [
  { color: "#A78BFA", geometry: "box", offset: [0.62, 0.42, 0] },
  { color: "#ffffff", geometry: "octa", offset: [-0.62, 0.42, 0] },
  { color: "#A78BFA", geometry: "dodeca", offset: [-0.62, -0.42, 0] },
  { color: "#ffffff", geometry: "tetra", offset: [0.62, -0.42, 0] },
];

function ToolGeometry({ type }) {
  if (type === "box") return <boxGeometry args={[0.34, 0.26, 0.22]} />;
  if (type === "octa") return <octahedronGeometry args={[0.2, 0]} />;
  if (type === "dodeca") return <dodecahedronGeometry args={[0.2, 0]} />;
  return <tetrahedronGeometry args={[0.23, 0]} />;
}

function UnifiedToolchain() {
  const rig = useRef();
  const core = useRef();
  const ring = useRef();
  const modules = useRef();
  const packets = useRef();
  const connections = useRef();
  const linePositions = useMemo(() => new Float32Array(TOOL_MODULES.length * 6), []);

  useFrame((state, delta) => {
    const t = state.clock.elapsedTime;
    const cycle = (t % 8) / 8;
    const dockIn = THREE.MathUtils.smoothstep(cycle, 0.54, 0.72);
    const dockOut = THREE.MathUtils.smoothstep(cycle, 0.86, 0.98);
    const dock = dockIn * (1 - dockOut);
    const corePulse = 1 + dock * 0.18 + Math.sin(t * 2.2) * 0.025;

    core.current.rotation.y += delta * (0.22 + dock * 0.5);
    core.current.rotation.x -= delta * 0.08;
    core.current.scale.setScalar(corePulse);
    ring.current.rotation.z -= delta * 0.1;
    ring.current.rotation.y += delta * 0.07;

    modules.current.children.forEach((module, index) => {
      const angle = t * 0.38 + index * (Math.PI / 2);
      const radius = 1.34;
      const orbitPosition = new THREE.Vector3(
        Math.cos(angle) * radius,
        Math.sin(angle) * radius * 0.64,
        Math.sin(angle * 1.35 + index) * 0.42,
      );
      const dockPosition = new THREE.Vector3(...TOOL_MODULES[index].offset);
      module.position.lerpVectors(orbitPosition, dockPosition, dock);
      module.rotation.x += delta * (0.34 + index * 0.06);
      module.rotation.y += delta * (0.44 + index * 0.05);
      module.scale.setScalar(1 + dock * 0.18);

      const connectionOffset = index * 6;
      linePositions[connectionOffset] = 0;
      linePositions[connectionOffset + 1] = 0;
      linePositions[connectionOffset + 2] = 0;
      linePositions[connectionOffset + 3] = module.position.x;
      linePositions[connectionOffset + 4] = module.position.y;
      linePositions[connectionOffset + 5] = module.position.z;

      const packet = packets.current.children[index];
      const packetProgress = (t * 0.42 + index * 0.21) % 1;
      packet.position.copy(module.position).multiplyScalar(1 - packetProgress);
      packet.scale.setScalar(0.7 + Math.sin(packetProgress * Math.PI) * 0.65);
    });

    connections.current.attributes.position.needsUpdate = true;
  });

  return (
    <group ref={rig}>
      <group ref={core}>
        <mesh>
          <icosahedronGeometry args={[0.43, 1]} />
          <meshStandardMaterial
            color="#111119"
            emissive="#271747"
            emissiveIntensity={0.24}
            metalness={0.82}
            roughness={0.22}
          />
        </mesh>
        <mesh scale={1.06}>
          <icosahedronGeometry args={[0.43, 1]} />
          <meshBasicMaterial color="#ffffff" wireframe transparent opacity={0.58} />
        </mesh>
      </group>

      <lineSegments>
        <bufferGeometry ref={connections}>
          <bufferAttribute attach="attributes-position" args={[linePositions, 3]} />
        </bufferGeometry>
        <lineBasicMaterial color="#A78BFA" transparent opacity={0.44} />
      </lineSegments>

      <group ref={modules}>
        {TOOL_MODULES.map((module, index) => (
          <mesh key={`tool-module-${module.geometry}`}>
            <ToolGeometry type={module.geometry} />
            <meshStandardMaterial
              color={module.color}
              emissive={index % 2 ? "#222229" : "#5B3AA8"}
              emissiveIntensity={0.58}
              metalness={0.58}
              roughness={0.24}
            />
          </mesh>
        ))}
      </group>

      <group ref={packets}>
        {TOOL_MODULES.map((module, index) => (
          <mesh key={`tool-packet-${index}`}>
            <sphereGeometry args={[0.04, 10, 10]} />
            <meshBasicMaterial color={module.color} />
          </mesh>
        ))}
      </group>

      <group ref={ring} position={[0, 0, -0.2]} rotation={[0.4, 0.18, 0]}>
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[1.48, 0.008, 6, 96]} />
          <meshBasicMaterial color="#A78BFA" transparent opacity={0.35} />
        </mesh>
        <mesh rotation={[Math.PI / 2.65, 0.2, Math.PI / 3]}>
          <torusGeometry args={[1.12, 0.007, 6, 96]} />
          <meshBasicMaterial color="#ffffff" transparent opacity={0.16} />
        </mesh>
      </group>
    </group>
  );
}

const SCENES = { torus: TorusKnot, pulse: PulseCore, orbit: UnifiedToolchain };

export default function ChapterScene({ variant, testId }) {
  const Scene = SCENES[variant] || TorusKnot;
  const { hostRef, frameloop } = useCanvasActivity();
  return (
    <div ref={hostRef} className="pointer-events-none h-48 sm:h-56 w-full" data-testid={testId}>
      <Canvas camera={{ position: [0, 0, 3.4], fov: 45 }} frameloop={frameloop} gl={{ alpha: true, antialias: true, stencil: false }} dpr={[1, 1.2]}>
        <ambientLight intensity={0.72} />
        <directionalLight position={[3, 4, 5]} intensity={2.2} color="#ffffff" />
        <pointLight position={[-2, -1, 2]} intensity={4.5} distance={5} color="#A78BFA" />
        <Scene />
      </Canvas>
    </div>
  );
}
